// JobDetailLayout.js
// Shared editorial "dossier" layout for individual job postings.
// Presentation only - each job page passes its own content/data.
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

const pad = (n) => String(n + 1).padStart(2, '0');

const JobDetailLayout = ({
  breadcrumbLabel,
  eyebrow = 'Careers at B & N Enterprise',
  title,
  meta = [],
  applyLink,
  applyHeading = 'Ready to apply?',
  applyText,
  recruitmentNote,
  sections = [],
  equalOpportunity,
}) => {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <section className="bg-white pt-12 md:pt-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-2 text-sm text-bnBody mb-12">
            <Link to="/" className="hover:text-bnAccent transition-colors">Home</Link>
            <span className="text-bnBorder">/</span>
            <Link to="/careers" className="hover:text-bnAccent transition-colors">Careers</Link>
            <span className="text-bnBorder">/</span>
            <span className="text-bnInk">{breadcrumbLabel}</span>
          </div>

          <div className="grid lg:grid-cols-12 gap-8 items-end">
            <div className="lg:col-span-9">
              <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-6">
                {eyebrow}
              </p>
              <h1 className="font-display font-light text-bnInk leading-[1.02] text-5xl md:text-6xl lg:text-7xl tracking-tight">
                {title}
              </h1>
            </div>
            <div className="lg:col-span-3 lg:text-right">
              <Link
                to={applyLink}
                className="inline-flex items-center gap-2 bg-bnAccent hover:bg-bnAccentDark text-white font-medium text-sm px-7 py-3.5 rounded-full transition-colors"
              >
                Apply Now <ArrowRight size={16} />
              </Link>
            </div>
          </div>

          {/* meta strip */}
          <div className="mt-14 border-t border-bnBorder pt-6 flex flex-wrap gap-x-14 gap-y-4">
            {meta.map((m) => (
              <div key={m.label}>
                <p className="text-bnAccent text-[11px] tracking-[0.2em] uppercase mb-1">{m.label}</p>
                <p className="text-bnInk text-sm md:text-base">{m.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            {sections.map((s, i) => (
              <div key={s.title} className="grid grid-cols-[auto,1fr] gap-5 md:gap-8 py-9 border-t border-bnBorder first:border-0 first:pt-0">
                <span className="font-display font-light text-bnAccent text-2xl md:text-3xl leading-none">{pad(i)}</span>
                <div>
                  <h2 className="font-display font-light text-bnInk text-2xl md:text-3xl mb-5">{s.title}</h2>
                  {s.paragraphs && s.paragraphs.map((p, j) => (
                    <p key={j} className="text-bnBody text-base leading-relaxed mb-4 last:mb-0 max-w-2xl">{p}</p>
                  ))}
                  {s.items && (
                    <ul className="max-w-2xl">
                      {s.items.map((it, j) => (
                        <li key={j} className="flex items-start gap-3.5 py-3 border-b border-bnBorder/70 last:border-0">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-bnAccent shrink-0" />
                          <span className="text-bnBody text-base leading-relaxed">{it}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}

            {equalOpportunity && (
              <div className="mt-10 bg-bnCream border border-bnBorder rounded-2xl p-8">
                <h3 className="font-display font-light text-bnInk text-xl md:text-2xl mb-3">Equal Opportunity Employer</h3>
                <p className="text-bnBody text-base leading-relaxed">{equalOpportunity}</p>
              </div>
            )}

            {/* Apply */}
            <div className="mt-14 pt-10 border-t border-bnBorder">
              <h2 className="font-display font-light text-bnInk text-3xl md:text-4xl mb-4">{applyHeading}</h2>
              {applyText && <p className="text-bnBody text-base leading-relaxed mb-8 max-w-xl">{applyText}</p>}
              <Link
                to={applyLink}
                className="inline-flex items-center gap-2 bg-bnAccent hover:bg-bnAccentDark text-white font-medium text-base px-8 py-4 rounded-full transition-colors"
              >
                Apply Now <ArrowRight size={18} />
              </Link>
              <p className="text-bnBody text-sm mt-4">Applications are reviewed on a rolling basis.</p>
              {recruitmentNote && (
                <p className="text-bnBody text-sm leading-relaxed mt-6 max-w-2xl">{recruitmentNote}</p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA: dark editorial band */}
      <section className="bg-bnInk py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="text-bnAccent text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-6">Join Our Team</p>
            <h2 className="font-display font-light text-white leading-[1.05] text-4xl md:text-5xl">
              Explore other career opportunities
            </h2>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link to="/careers" className="inline-flex items-center gap-2 bg-bnAccent hover:bg-bnAccentDark text-white font-medium text-sm px-7 py-3.5 rounded-full transition-colors">
              View All Positions <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JobDetailLayout;
